// VH-229 refinement: the actions of one cycle week's plan — same as last week, a new plan under review
// (preview: generate, adjust, edit, accept, discard). Pure hooks; the week panel renders them.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { planDocumentAPI } from '../../services/api'
import { contentLanguage } from '../../i18n/supported'

export const POLL_MS = 3000
export const uiLanguage = contentLanguage

export function usePlanPreview(cycleId, planId, enabled = true) {
  return useQuery({
    queryKey: ['planPreview', cycleId, planId],
    queryFn: () => planDocumentAPI.getPreview(cycleId, planId).then((r) => r.data)
      .catch((err) => { if (err?.response?.status === 404) return null; throw err }),
    enabled: !!cycleId && !!planId && enabled,
    refetchInterval: (query) => (query.state.data?.state === 'generating' ? POLL_MS : false),
    retry: false,
  })
}

export function useWeekPlanning({ cycleId, week, onError }) {
  const queryClient = useQueryClient()
  const planId = week.plan_id
  const refreshWeek = () => {
    queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] })
    queryClient.invalidateQueries({ queryKey: ['planDocument', cycleId, planId] })
    queryClient.invalidateQueries({ queryKey: ['planMeals'] })
  }
  const setPreview = (data) => queryClient.setQueryData(['planPreview', cycleId, planId], data)

  const copyPrevious = useMutation({
    mutationFn: (replace = false) => planDocumentAPI.copyPrevious(cycleId, week.week_start, replace),
    onSuccess: refreshWeek,
    onError,
  })
  const generate = useMutation({
    mutationFn: ({ language, instruction = null }) => planDocumentAPI.startPreview(cycleId, planId, language, instruction),
    onSuccess: (r) => { setPreview(r.data); queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] }) },
    onError,
  })
  const adjust = useMutation({
    mutationFn: () => planDocumentAPI.adjust(cycleId, planId),
    onSuccess: (r) => setPreview(r.data),
    onError,
  })
  const edit = useMutation({
    mutationFn: (edits) => planDocumentAPI.editPreview(cycleId, planId, edits),
    onSuccess: (r) => setPreview(r.data),
    onError,
  })
  const accept = useMutation({
    mutationFn: () => planDocumentAPI.acceptPreview(cycleId, planId),
    onSuccess: () => { setPreview(null); refreshWeek() },
    onError,
  })
  const discard = useMutation({
    mutationFn: () => planDocumentAPI.discardPreview(cycleId, planId),
    onSuccess: () => { setPreview(null); queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] }) },
    onError,
  })
  return { copyPrevious, generate, adjust, edit, accept, discard }
}
